import { useContext, useState, useMemo, useEffect, useRef } from 'react'
import { SessionDataContext } from '../context/SessionDataContext'
import { TelemetryDataContext } from '../context/TelemetryDataContext'
import { StudentAnalyticsContext } from '../context/StudentAnalyticsContext'
import { StudentDetail } from './StudentDetail'
import { TAXONOMY, WINDOW_TAXONOMY, getStudentCategories, getStudentQueryWindows } from './studentTaxonomy'



// ─── Behavior Bar ─────────────────────────────────────────────────────────────

function BehaviorBar({ segments, totalDuration, compact = false }) {
  if (!segments || segments.length === 0 || !totalDuration) {
    return <div style={{ height: compact ? '10px' : '16px', borderRadius: '4px', backgroundColor: '#f0f0f2', width: '100%' }} />
  }
  return (
    <div style={{ display: 'flex', height: compact ? '10px' : '16px', borderRadius: '4px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', width: '100%' }}>
      {segments.map((seg, i) => {
        const dur = seg.endTime - seg.startTime
        const pct = (dur / totalDuration) * 100
        if (pct < 0.3) return null
        const color = seg.behavior?.color || '#9ca3af'
        return (
          <div key={i} title={`${seg.behavior?.label || 'Unknown'} (${Math.round(dur / 1000)}s)`}
            style={{ width: `${pct}%`, backgroundColor: color, minWidth: '2px', borderRight: i < segments.length - 1 ? '1px solid rgba(255,255,255,0.25)' : 'none' }}
          />
        )
      })}
    </div>
  )
}

// ─── Query Windows Bar ────────────────────────────────────────────────────────

function QueryWindowsBar({ windows, totalDuration }) {
  if (!windows || windows.length === 0 || !totalDuration) {
    return <div style={{ height: '10px', borderRadius: '4px', backgroundColor: '#f5f5f7', width: '100%' }} />
  }
  return (
    <div style={{ display: 'flex', height: '10px', borderRadius: '4px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', width: '100%' }}>
      {windows.map((w, i) => {
        const pct = ((w.endTime - w.startTime) / totalDuration) * 100
        if (pct < 0.3) return null
        const tax = TAXONOMY[w.category]
        return (
          <div key={i}
            title={`Window ${w.windowIndex === 0 ? 'Pre-Query' : `Q${w.windowIndex}`}: ${tax?.label || w.category}`}
            style={{
              width: `${pct}%`, minWidth: '2px',
              backgroundColor: tax?.color || '#9ca3af',
              borderRight: i < windows.length - 1 ? '1px solid rgba(255,255,255,0.3)' : 'none',
            }}
          />
        )
      })}
    </div>
  )
}

// ─── Legends ──────────────────────────────────────────────────────────────────

const BEHAVIOR_LEGEND = [
  { label: 'Implementing', color: '#22c55e' },
  { label: 'Thinking',     color: '#3b82f6' },
  { label: 'Seeking Help', color: '#a855f7' },
  { label: 'Debugging',    color: '#ef4444' },
  { label: 'Idle',         color: '#d1d5db' },
]

function SequenceLegend({ mode }) {
  const items = (mode === 'behavior' || mode === 'pre-query')
    ? BEHAVIOR_LEGEND
    : Object.entries(TAXONOMY).map(([, v]) => ({ label: v.label, color: v.color }))

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', padding: '10px 0 4px', borderBottom: '1px solid #f0f0f2', marginBottom: '12px' }}>
      {items.map(b => (
        <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: b.color, border: '1px solid rgba(0,0,0,0.08)' }} />
          <span style={{ fontSize: '11px', color: '#888' }}>{b.label}</span>
        </div>
      ))}
    </div>
  )
}

// ─── Toggle Switch ────────────────────────────────────────────────────────────

function ViewToggle({ value, onChange, opts }) {
  return (
    <div style={{ display: 'flex', background: '#f0f0f2', borderRadius: '8px', padding: '3px', gap: '2px' }}>
      {opts.map(o => (
        <button key={o.key} onClick={() => onChange(o.key)} style={{
          padding: '5px 12px', fontSize: '12px',
          fontWeight: value === o.key ? 600 : 400,
          borderRadius: '6px', border: 'none', cursor: 'pointer',
          background: value === o.key ? 'white' : 'transparent',
          color: value === o.key ? '#111' : '#888',
          boxShadow: value === o.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
          transition: 'all 0.15s',
        }}>
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ─── Adapter ──────────────────────────────────────────────────────────────────

function buildStudentDetailData(student, events, sessionData) {
  const name = student.display_name || student.username || `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Unknown'
  if (!events || events.length === 0) {
    return {
      id: student.id, name, hasLlmMessage: false, llmMessageCount: 0, passedAllTests: false,
      submissionTime: new Date().toISOString(), testsPassed: 0, duration: 0,
      conversation: [], replayEvents: [], behavior: {},
      aiUsage: { totalQueries: 0, avgQueryLengthWords: 0, timeToFirstQuery: 0, chatToCodeLatency: 0, errorToQueryLatency: 0, iDontKnowRatio: 0, specificQuestionRatio: 0 },
      timeToFirstRun: 0, timeToCompletion: 0, behavioralSummary: 'No telemetry data available.',
    }
  }
  const startTime = events[0].timestamp
  const lastEvent = events[events.length - 1]
  const duration = Math.round((lastEvent.timestamp - startTime) / 1000)
  const typeMap = {
    CODE_TYPE: 'keystroke', CODE_INDENT: 'keystroke', CODE_PASTE: 'paste', CODE_CUT: 'delete',
    CODE_DELETE: 'delete', CODE_DELETE_SELECTION: 'delete', CODE_UNDO: 'keystroke', CODE_REDO: 'keystroke',
    TERMINAL_RUN: 'run', TERMINAL_ERROR: 'error', TEST_CASE_RESULT: 'submit',
    CHAT_QUERY: 'ai-query', CHAT_RESPONSE: 'ai-query', TAB_STATE: 'tab-switch',
  }
  const segmentMap = {
    CODE_TYPE: 'coding', CODE_INDENT: 'coding', CODE_PASTE: 'coding', CODE_DELETE: 'coding',
    CODE_DELETE_SELECTION: 'coding', CODE_CUT: 'coding', CODE_UNDO: 'coding', CODE_REDO: 'coding',
    TERMINAL_RUN: 'testing', TERMINAL_ERROR: 'testing', TERMINAL_OUTPUT: 'testing', TEST_CASE_RESULT: 'testing',
    CHAT_QUERY: 'ai-interaction', CHAT_RESPONSE: 'ai-interaction', CHAT_TYPE: 'ai-interaction', CHAT_SEND: 'ai-interaction',
    MOUSE_MOVE: 'reading', MOUSE_CLICK: 'reading', TAB_STATE: 'idle',
  }
  const replayEvents = events.filter(e => typeMap[e.type]).map(e => ({
    time: Math.round((e.timestamp - startTime) / 1000), type: typeMap[e.type],
    description: formatEventDescription(e), segment: segmentMap[e.type] || 'coding',
  }))
  const chatEvents = events.filter(e => e.type === 'CHAT_QUERY' || e.type === 'CHAT_RESPONSE')
  const conversation = chatEvents.map(e => ({
    role: e.type === 'CHAT_QUERY' ? 'user' : 'assistant',
    content: e.payload?.text || e.payload?.message || e.payload?.content || '',
    timestamp: e.timestamp,
  })).sort((a, b) => a.timestamp - b.timestamp)
  const queries = chatEvents.filter(e => e.type === 'CHAT_QUERY')
  const firstQuery = queries[0]
  const timeToFirstQuery = firstQuery ? Math.round((firstQuery.timestamp - startTime) / 1000) : 0
  const testResults = events.filter(e => e.type === 'TEST_CASE_RESULT')
  const lastTest = testResults[testResults.length - 1]
  const testsPassed = lastTest?.payload?.passed_count || 0
  const testsTotal = lastTest?.payload?.total_tests || 0
  const passedAll = testsTotal > 0 && testsPassed === testsTotal
  const firstRun = events.find(e => e.type === 'TERMINAL_RUN')
  const timeToFirstRun = firstRun ? Math.round((firstRun.timestamp - startTime) / 1000) : 0
  const firstAllPass = events.find(e => e.type === 'TEST_CASE_RESULT' && e.payload?.passed_count === e.payload?.total_tests && e.payload?.total_tests > 0)
  const timeToCompletion = firstAllPass ? Math.round((firstAllPass.timestamp - startTime) / 1000) : duration
  const avgQueryLen = queries.length > 0 ? Math.round(queries.reduce((sum, q) => sum + (q.payload?.text || q.payload?.message || q.payload?.content || '').split(/\s+/).length, 0) / queries.length) : 0
  const chatResponses = events.filter(e => e.type === 'CHAT_RESPONSE')
  let chatToCodeTotal = 0, chatToCodeCount = 0
  chatResponses.forEach(r => { const nextCode = events.find(e => e.timestamp > r.timestamp && e.type?.startsWith('CODE_')); if (nextCode) { chatToCodeTotal += (nextCode.timestamp - r.timestamp) / 1000; chatToCodeCount++ } })
  const errors = events.filter(e => e.type === 'TERMINAL_ERROR')
  let errToQueryTotal = 0, errToQueryCount = 0
  errors.forEach(err => { const nextQuery = events.find(e => e.timestamp > err.timestamp && e.type === 'CHAT_QUERY'); if (nextQuery) { errToQueryTotal += (nextQuery.timestamp - err.timestamp) / 1000; errToQueryCount++ } })
  const summaryParts = [`Completed in ${Math.round(duration / 60)}m.`]
  summaryParts.push(passedAll ? `Passed all ${testsTotal} tests.` : `Passed ${testsPassed}/${testsTotal} tests.`)
  summaryParts.push(queries.length > 0 ? `Used AI ${queries.length} time${queries.length !== 1 ? 's' : ''}, first at ${Math.round(timeToFirstQuery / 60)}m.` : 'Did not use AI assistance.')
  if (errors.length > 0) summaryParts.push(`Encountered ${errors.length} error${errors.length !== 1 ? 's' : ''}.`)
  return {
    id: student.id, name, hasLlmMessage: queries.length > 0, llmMessageCount: queries.length, passedAllTests: passedAll,
    submissionTime: new Date(lastEvent.timestamp).toISOString(), testsPassed, duration, conversation, replayEvents,
    behavior: { tabSwitches: events.filter(e => e.type === 'TAB_STATE').length, idleTime: 0, longestIdle: 0, copyPasteCount: events.filter(e => e.type === 'CODE_PASTE').length, keystrokeRate: 0, focusLossCount: 0, totalEdits: events.filter(e => e.type === 'CODE_TYPE').length, totalRuns: events.filter(e => e.type === 'TERMINAL_RUN').length, totalErrors: errors.length, netCodeGrowth: 0, deleteTypeRatio: 0, meanTimeBetweenRuns: 0, consecutiveErrors: 0, timeByRegion: { codeEditor: 0, chat: 0, terminal: 0, taskDescription: 0, testPanel: 0 } },
    aiUsage: { totalQueries: queries.length, avgQueryLengthWords: avgQueryLen, timeToFirstQuery, chatToCodeLatency: chatToCodeCount > 0 ? Math.round(chatToCodeTotal / chatToCodeCount) : 0, errorToQueryLatency: errToQueryCount > 0 ? Math.round(errToQueryTotal / errToQueryCount) : 0, iDontKnowRatio: 0, specificQuestionRatio: 0 },
    timeToFirstRun, timeToCompletion, behavioralSummary: summaryParts.join(' '),
  }
}

function formatEventDescription(event) {
  const { type, payload } = event
  switch (type) {
    case 'CODE_TYPE': return payload?.changes?.[0]?.text?.slice(0, 30) || 'typed'
    case 'CODE_PASTE': return 'Pasted code'
    case 'CODE_DELETE': case 'CODE_DELETE_SELECTION': return 'Deleted code'
    case 'CODE_CUT': return 'Cut code'
    case 'CODE_UNDO': return 'Undo'
    case 'CODE_REDO': return 'Redo'
    case 'CODE_INDENT': return 'Indented'
    case 'TERMINAL_RUN': return `Ran code (${payload?.code_length || 0} chars)`
    case 'TERMINAL_ERROR': return `${payload?.type || 'Error'}: ${(payload?.message || '').slice(0, 40)}`
    case 'TEST_CASE_RESULT': return `Tests: ${payload?.passed_count}/${payload?.total_tests} passed`
    case 'CHAT_QUERY': return (payload?.text || payload?.message || '').slice(0, 50)
    case 'CHAT_RESPONSE': return 'AI responded'
    case 'TAB_STATE': return payload?.visible ? 'Tab visible' : 'Tab hidden'
    default: return type
  }
}

// ─── Mini Badge ───────────────────────────────────────────────────────────────

function MiniBadge({ label, value }) {
  if (value == null || value === 0) return null
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 500, color: '#555', padding: '2px 7px', borderRadius: '5px', background: '#f3f4f6', border: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
      <span style={{ fontSize: '10px', color: '#999' }}>{label}</span>
      {value}
    </span>
  )
}

// ─── Sequence Row ─────────────────────────────────────────────────────────────

function StudentSequenceRow({ student, segments, totalDuration, studentAnalytics, sequenceMode, studentCategory, onClick, getDisplayName, formatDuration }) {
  const [hovered, setHovered] = useState(false)
  const hasData = segments && segments.length > 0 && totalDuration > 0

  const runs        = studentAnalytics?.sessionStats?.full?.frequency?.terminalRun?.count ?? null
  const errors      = studentAnalytics?.sessionStats?.full?.frequency?.terminalError?.count ?? null
  const llm         = studentAnalytics?.sessionStats?.full?.frequency?.chatQuery?.count ?? student.chat_count ?? null
  const testsPassed = student.tests_passed ?? null
  const testsTotal  = student.tests_total ?? null

  const queryWindows = useMemo(() => getStudentQueryWindows(studentAnalytics), [studentAnalytics])
  const preQueryWindow = queryWindows[0] || null
  const categories = Array.isArray(studentCategory) ? studentCategory : (studentCategory ? [studentCategory] : [])

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'grid', gridTemplateColumns: '160px 1fr auto', alignItems: 'center', gap: '14px',
        padding: '12px 14px', borderRadius: '8px',
        border: `1px solid ${hovered ? '#a8c8e8' : '#e8e8eb'}`,
        background: hovered ? '#f0f6ff' : 'white',
        cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s',
      }}
    >
      {/* Name + duration */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {getDisplayName(student)}
        </div>
        {student.duration != null && (
          <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>{formatDuration(student.duration)}</div>
        )}
      </div>

      {/* Bar — behavior, all windows, or pre-query only */}
      <div>
        {sequenceMode === 'windows' ? (
          queryWindows.length > 0
            ? <QueryWindowsBar windows={queryWindows} totalDuration={totalDuration} />
            : <div style={{ height: '10px', borderRadius: '4px', backgroundColor: '#f5f5f7' }} />
        ) : sequenceMode === 'pre-query' ? (
          (() => {
            const beforeSegs = studentAnalytics?.beforeSegments?.filter(s => !s.isSystemEvent) || []
            const beforeDur = studentAnalytics?.firstQueryMs || totalDuration
            return beforeSegs.length > 0
              ? <BehaviorBar segments={beforeSegs} totalDuration={beforeDur} compact />
              : <div style={{ height: '10px', borderRadius: '4px', backgroundColor: '#f5f5f7' }} />
          })()
        ) : (
          hasData
            ? <BehaviorBar segments={segments} totalDuration={totalDuration} compact />
            : <div style={{ height: '10px', borderRadius: '4px', backgroundColor: '#f5f5f7', display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
                <span style={{ fontSize: '10px', color: '#ccc' }}>no data</span>
              </div>
        )}
      </div>

      {/* Stat badges */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
        {testsTotal != null && testsTotal > 0 && <MiniBadge label="tests" value={`${testsPassed ?? 0}/${testsTotal}`} />}
        <MiniBadge label="runs" value={runs} />
        <MiniBadge label="errors" value={errors} />
        <MiniBadge label="llm" value={llm} />
      </div>
    </div>
  )
}

// ─── Group Analytics ─────────────────────────────────────────────────────────

// ── GroupCharts ────────────────────────────────────────────────────────────
// Static data — pre-query groups ordered by effort score
const PQ_DATA = [
  { key:'ask-extended',   label:'AWT\n(Extended)', fullLabel:'Ask Without Trying (Extended)', n:10,  effort:0.000, pass:100.0, dur:469,  queries:4.0, errors:0.0, passive:48.0, declining:10,  increasing:76 },
  { key:'code-no-test',   label:'Wrote Code\nNever Tested',  fullLabel:'Wrote Code, Never Tested',      n:19,  effort:0.152, pass:84.2,  dur:460,  queries:3.8, errors:0.0, passive:27.8, declining:24,  increasing:52 },
  { key:'ran-no-debug',   label:'Ran Tests\nNo Debug',       fullLabel:'Ran Tests, No Debug',           n:13,  effort:0.131, pass:92.3,  dur:522,  queries:6.1, errors:0.7, passive:36.7, declining:8,   increasing:69 },
  { key:'debugged-tested',label:'Debugged\n& Tested',        fullLabel:'Debugged and Tested',           n:30,  effort:0.318, pass:90.0,  dur:593,  queries:4.7, errors:1.2, passive:28.1, declining:52,  increasing:24 },
  { key:'debug-loop',     label:'Debug\nLoop',               fullLabel:'Debug Loop',                    n:15,  effort:0.334, pass:66.7,  dur:720,  queries:4.0, errors:6.3, passive:24.6, declining:60,  increasing:7  },
]
// Sort by effort for ordering
PQ_DATA.sort((a,b) => a.effort - b.effort)

const FS_DATA = [
  { key:'ask-extended',      label:'AWT\n(Extended)',          fullLabel:'Ask Without Trying (Extended)',  n:47,  effort:0.225, pass:59.6,  dur:584,  queries:4.7, errors:1.9, passive:44.8, declining:32, increasing:43 },
  { key:'repeated-no-effort',label:'Repeated\nQueries',        fullLabel:'Repeated Queries Without Trying',n:19,  effort:0.265, pass:89.5,  dur:575,  queries:7.3, errors:0.7, passive:53.5, declining:22, increasing:57 },
  { key:'ran-no-debug',      label:'Ran Tests\nNo Debug',      fullLabel:'Ran Tests, No Debug',            n:38,  effort:0.265, pass:89.5,  dur:347,  queries:2.8, errors:0.5, passive:37.8, declining:14, increasing:57 },
  { key:'ask-quick',         label:'AWT',                      fullLabel:'Ask Without Trying',             n:53,  effort:0.293, pass:84.9,  dur:534,  queries:5.7, errors:1.3, passive:42.6, declining:28, increasing:44 },
  { key:'code-no-test',      label:'Wrote Code\nNever Tested', fullLabel:'Wrote Code, Never Tested',       n:21,  effort:0.295, pass:85.7,  dur:454,  queries:3.9, errors:0.0, passive:28.1, declining:26, increasing:48 },
  { key:'query-burst',       label:'Query\nBurst',             fullLabel:'Query Burst',                    n:38,  effort:0.305, pass:86.8,  dur:584,  queries:7.1, errors:1.8, passive:44.5, declining:30, increasing:44 },
  { key:'no-ai',             label:'No AI\nUsed',              fullLabel:'No AI Used',                     n:103, effort:0.365, pass:79.6,  dur:351,  queries:0.0, errors:null,passive:0.0,  declining:0,  increasing:100 },
  { key:'debug-loop',        label:'Debug\nLoop',              fullLabel:'Debug Loop',                     n:61,  effort:0.368, pass:85.2,  dur:593,  queries:2.7, errors:3.2, passive:28.8, declining:47, increasing:30 },
  { key:'debugged-tested',   label:'Debugged\n& Tested',       fullLabel:'Debugged and Tested',            n:119, effort:0.383, pass:89.9,  dur:483,  queries:3.1, errors:1.8, passive:28.7, declining:37, increasing:38 },
]
FS_DATA.sort((a,b) => a.effort - b.effort)

// Color ramp: red → orange → yellow → light green → green based on position
function effortColor(i, total) {
  const t = total <= 1 ? 1 : i / (total - 1)
  if (t < 0.25) return '#ef4444'
  if (t < 0.5)  return '#f97316'
  if (t < 0.7)  return '#eab308'
  if (t < 0.85) return '#86efac'
  return '#22c55e'
}

function VerticalBarChart({ data, getValue, formatVal, yLabel, title, showEffortAxis, height = 240 }) {
  const BAR_W = 60
  const GAP   = 12
  const PAD_L = 52
  const PAD_R = 16
  const PAD_T = 36
  const PAD_B = 68
  const n = data.length
  const W = PAD_L + n * BAR_W + (n - 1) * GAP + PAD_R
  const H = PAD_T + height + PAD_B

  const vals = data.map(d => getValue(d)).filter(v => v != null)
  const maxV = vals.length > 0 ? Math.max(...vals) * 1.15 : 1
  const minV = 0

  const barH = (v) => v == null ? 0 : Math.max(3, ((v - minV) / (maxV - minV)) * height)
  const barX = (i) => PAD_L + i * (BAR_W + GAP)
  const barY = (v) => PAD_T + height - barH(v)

  // Y axis ticks
  const ticks = 4
  const yTicks = Array.from({length: ticks + 1}, (_, i) => minV + (maxV - minV) * i / ticks)

  return (
    <div style={{ marginBottom: '28px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: '#222', marginBottom: '4px', textAlign: 'center' }}>{title}</div>
      {showEffortAxis && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 0, marginBottom: '2px', paddingLeft: PAD_L + 'px', paddingRight: PAD_R + 'px' }}>
          {data.map((d, i) => (
            <div key={i} style={{ width: BAR_W + (i < n-1 ? GAP : 0) + 'px', textAlign: 'center', fontSize: '9px', color: '#aaa' }}>
              {d.effort.toFixed(3)}
            </div>
          ))}
        </div>
      )}
      <svg width={W} height={H} style={{ display: 'block', margin: '0 auto', overflow: 'visible' }}>
        {/* Y axis ticks */}
        {yTicks.map((t, i) => {
          const y = PAD_T + height - ((t - minV) / (maxV - minV)) * height
          return (
            <g key={i}>
              <line x1={PAD_L - 4} y1={y} x2={PAD_L} y2={y} stroke="#e5e7eb" strokeWidth={1} />
              <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#f3f4f6" strokeWidth={1} />
              <text x={PAD_L - 6} y={y + 4} textAnchor="end" fontSize={9} fill="#bbb">
                {typeof t === 'number' && t >= 100 ? Math.round(t) : t.toFixed(t < 1 ? 1 : 0)}
              </text>
            </g>
          )
        })}
        {/* Axes */}
        <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + height} stroke="#e5e7eb" strokeWidth={1} />
        <line x1={PAD_L} y1={PAD_T + height} x2={W - PAD_R} y2={PAD_T + height} stroke="#e5e7eb" strokeWidth={1} />

        {/* Dashed trend line */}
        {data.map((d, i) => {
          const v = getValue(d)
          if (v == null || i === 0) return null
          const prev = getValue(data[i-1])
          if (prev == null) return null
          return (
            <line key={i}
              x1={barX(i-1) + BAR_W/2} y1={barY(prev)}
              x2={barX(i)   + BAR_W/2} y2={barY(v)}
              stroke="#9ca3af" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.7}
            />
          )
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const v = getValue(d)
          const x = barX(i)
          const bh = barH(v)
          const by = barY(v)
          const color = effortColor(i, n)
          return (
            <g key={i}>
              <rect x={x} y={by} width={BAR_W} height={bh} fill={color} rx={3} opacity={0.9} />
              {/* Value label on bar */}
              {v != null && (
                <text x={x + BAR_W/2} y={by - 4} textAnchor="middle" fontSize={10} fontWeight={700} fill="#333">
                  {formatVal(v)}
                </text>
              )}
              {/* X axis label — multi-line via tspan */}
              {d.label.split('\n').map((line, li) => (
                <text key={li} x={x + BAR_W/2} y={PAD_T + height + 14 + li * 11} textAnchor="middle" fontSize={9} fill="#555">
                  {line}
                </text>
              ))}
              {/* n label */}
              <text x={x + BAR_W/2} y={PAD_T + height + 14 + d.label.split('\n').length * 11} textAnchor="middle" fontSize={8} fill="#aaa">
                (n={d.n})
              </text>
            </g>
          )
        })}

        {/* Y axis label */}
        <text x={12} y={PAD_T + height/2} textAnchor="middle" fontSize={9} fill="#999" transform={`rotate(-90, 12, ${PAD_T + height/2})`}>
          {yLabel}
        </text>
      </svg>
    </div>
  )
}

function NetDirectionChart({ data, title, height = 220 }) {
  const BAR_W = 60
  const GAP   = 12
  const PAD_L = 52
  const PAD_R = 16
  const PAD_T = 24
  const PAD_B = 68
  const n = data.length
  const W = PAD_L + n * BAR_W + (n-1) * GAP + PAD_R

  const nets = data.map(d => d.increasing - d.declining)
  const maxAbs = Math.max(...nets.map(Math.abs), 10) * 1.2
  const midY = PAD_T + height / 2
  const H = PAD_T + height + PAD_B

  const barH = (v) => Math.max(2, (Math.abs(v) / maxAbs) * (height / 2))
  const barX = (i) => PAD_L + i * (BAR_W + GAP)

  return (
    <div style={{ marginBottom: '28px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: '#222', marginBottom: '4px', textAlign: 'center' }}>{title}</div>
      <svg width={W} height={H} style={{ display: 'block', margin: '0 auto', overflow: 'visible' }}>
        {/* Zero line */}
        <line x1={PAD_L} y1={midY} x2={W - PAD_R} y2={midY} stroke="#374151" strokeWidth={1.5} />
        <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + height} stroke="#e5e7eb" strokeWidth={1} />

        {/* Grid */}
        {[-50, 50].map(v => {
          const y = midY - (v / maxAbs) * (height / 2)
          return <line key={v} x1={PAD_L} y1={y} x2={W-PAD_R} y2={y} stroke="#f3f4f6" strokeWidth={1} />
        })}

        {data.map((d, i) => {
          const net = nets[i]
          const bh  = barH(net)
          const x   = barX(i)
          const by  = net >= 0 ? midY - bh : midY
          const color = net >= 0 ? '#22c55e' : '#ef4444'
          return (
            <g key={i}>
              <rect x={x} y={by} width={BAR_W} height={bh} fill={color} rx={3} opacity={0.9} />
              <text x={x + BAR_W/2} y={net >= 0 ? by - 4 : by + bh + 12} textAnchor="middle" fontSize={10} fontWeight={700} fill="#333">
                {net >= 0 ? `+${net}%` : `${net}%`}
              </text>
              {d.label.split('\n').map((line, li) => (
                <text key={li} x={x + BAR_W/2} y={PAD_T + height + 14 + li * 11} textAnchor="middle" fontSize={9} fill="#555">{line}</text>
              ))}
              <text x={x + BAR_W/2} y={PAD_T + height + 14 + d.label.split('\n').length * 11} textAnchor="middle" fontSize={8} fill="#aaa">(n={d.n})</text>
            </g>
          )
        })}
        <text x={12} y={PAD_T + height/2} textAnchor="middle" fontSize={9} fill="#999" transform={`rotate(-90, 12, ${PAD_T + height/2})`}>Net %</text>
      </svg>
    </div>
  )
}

function fmtDur(s) {
  if (s == null) return '—'
  return `${Math.floor(s/60)}m ${String(Math.round(s%60)).padStart(2,'0')}s`
}

function GroupCharts({ allStudents, analyticsById, sequenceMode }) {
  const section = sequenceMode === 'behavior' ? 'session-wide' : 'pre-query'
  const data = section === 'pre-query' ? PQ_DATA : FS_DATA
  return (
    <div style={{ padding: '4px 0', overflowY: 'auto', flex: 1 }}>
      {/* Chart grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px 24px' }}>
        <VerticalBarChart data={data} getValue={d=>d.effort} formatVal={v=>v.toFixed(3)} yLabel="Edits/sec" title={`${section === "pre-query" ? "Pre-Query" : "Session-Wide"}: Effort Score`} showEffortAxis={true} />
        <VerticalBarChart data={data} getValue={d=>d.pass} formatVal={v=>`${v}%`} yLabel="Pass Rate (%)" title={`${section === "pre-query" ? "Pre-Query" : "Session-Wide"}: Pass Rate`} height={200} />
        <VerticalBarChart data={data} getValue={d=>d.dur} formatVal={fmtDur} yLabel="Duration (s)" title={`${section === "pre-query" ? "Pre-Query" : "Session-Wide"}: Session Duration`} />
        <VerticalBarChart data={data} getValue={d=>d.queries} formatVal={v=>v.toFixed(1)} yLabel="Avg Queries" title={`${section === "pre-query" ? "Pre-Query" : "Session-Wide"}: Queries per Session`} />
        <VerticalBarChart data={data} getValue={d=>d.declining} formatVal={v=>`${v}%`} yLabel="% Declining" title={`${section === "pre-query" ? "Pre-Query" : "Session-Wide"}: % Students Declining in Effort`} />
        <NetDirectionChart data={data} title={`${section === "pre-query" ? "Pre-Query" : "Session-Wide"}: Net Effort Direction`} />
      </div>
    </div>
  )
}


function StatBlock({ label, value, sub }) {
  return (
    <div style={{ padding: '14px 16px', borderRadius: '8px', background: '#fafafa', border: '1px solid #f0f0f2' }}>
      <div style={{ fontSize: '11px', color: '#999', fontWeight: 500, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: '20px', fontWeight: 700, color: '#111', lineHeight: 1 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: '11px', color: '#aaa', marginTop: '4px' }}>{sub}</div>}
    </div>
  )
}

function SectionHeader({ title, open, onToggle }) {
  return (
    <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '18px 0 8px', borderBottom: '1px solid #f0f0f2', marginBottom: open ? '12px' : '0', cursor: 'pointer', userSelect: 'none' }}>
      {title}
      <span style={{ fontSize: '10px', color: '#aaa', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>{open ? '▲' : '▼'}</span>
    </div>
  )
}

function GroupAnalytics({ groupStudents, allStudents, taxonomyFilter, sequenceMode, analyticsById, TAXONOMY }) {
  const groupLabel = TAXONOMY[taxonomyFilter]?.label || 'All'
  const total = allStudents.length
  const n = groupStudents.length
  const pct = total > 0 ? Math.round((n / total) * 100) : 0

  // Collect per-student data
  const perStudent = groupStudents.map(s => {
    const a = analyticsById[s.id]?.analytics || analyticsById[String(s.id)]?.analytics || null
    return { student: s, analytics: a }
  }).filter(x => x.analytics)

  const avg = (arr) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : null
  const fmtSec = (s) => s == null ? '—' : s < 60 ? `${Math.round(s)}s` : `${Math.floor(s/60)}m ${Math.round(s%60)}s`
  const fmtNum = (n, dec = 1) => n == null ? '—' : Number.isInteger(n) ? n : n.toFixed(dec)

  // ── Before first query ────────────────────────────────────────────────────
  const beforeTimes   = perStudent.map(x => x.analytics.firstQueryMs != null ? x.analytics.firstQueryMs / 1000 : null).filter(v => v != null)
  const beforeRuns    = perStudent.map(x => {
    const fq = x.analytics.firstQueryMs
    if (fq == null) return null
    return (x.analytics.filteredEvents || []).filter(e => e.type === 'TERMINAL_RUN' && (e.timestamp - x.analytics.replayStartTime) < fq).length
  }).filter(v => v != null)
  const beforeErrors  = perStudent.map(x => {
    const fq = x.analytics.firstQueryMs
    if (fq == null) return null
    return (x.analytics.filteredEvents || []).filter(e => e.type === 'TERMINAL_ERROR' && (e.timestamp - x.analytics.replayStartTime) < fq).length
  }).filter(v => v != null)
  const beforeTests   = perStudent.map(x => {
    const fq = x.analytics.firstQueryMs
    if (fq == null) return null
    const testEvents = (x.analytics.filteredEvents || []).filter(e => e.type === 'TEST_CASE_RESULT' && (e.timestamp - x.analytics.replayStartTime) < fq)
    if (testEvents.length === 0) return null
    return testEvents[testEvents.length - 1].payload?.passed_count ?? null
  }).filter(v => v != null)

  // Aggregate pre-query behavior segments
  const behaviorCounts = {}
  perStudent.forEach(x => {
    const fq = x.analytics.firstQueryMs
    const segs = (x.analytics.beforeSegments || []).filter(s => !s.isSystemEvent)
    segs.forEach(s => {
      const id = s.behavior?.id || 'unknown'
      const label = s.behavior?.label || id
      const dur = s.endTime - s.startTime
      if (!behaviorCounts[id]) behaviorCounts[id] = { label, totalMs: 0, count: 0 }
      behaviorCounts[id].totalMs += dur
      behaviorCounts[id].count++
    })
  })
  const totalBehaviorMs = Object.values(behaviorCounts).reduce((sum, b) => sum + b.totalMs, 0)
  const behaviorBreakdown = Object.entries(behaviorCounts)
    .sort(([,a],[,b]) => b.totalMs - a.totalMs)
    .map(([id, b]) => ({ id, label: b.label, pct: totalBehaviorMs > 0 ? Math.round(b.totalMs / totalBehaviorMs * 100) : 0 }))

  // ── Query itself ──────────────────────────────────────────────────────────
  const allFirstQueries = perStudent.map(x => {
    const fq = x.analytics.firstQueryEvent
    if (!fq) return null
    return fq.payload?.text || fq.payload?.message || fq.payload?.content || null
  }).filter(Boolean)

  const avgQueryLen = avg(allFirstQueries.map(q => q.length))

  // Sample up to 4 shortest representative queries (non-trivial length)
  const sampleQueries = allFirstQueries
    .filter(q => q.length > 20)
    .sort((a, b) => a.length - b.length)
    .slice(0, 4)
    .map(q => q.length > 120 ? q.slice(0, 120) + '…' : q)

  // ── After first query ─────────────────────────────────────────────────────
  const firstBehaviorAfter = {}
  perStudent.forEach(x => {
    const fqEvent = x.analytics.firstQueryEvent
    if (!fqEvent) return
    // Find first response, then first segment after it
    const events = x.analytics.filteredEvents || []
    const responseEvent = events.find(e => e.type === 'CHAT_RESPONSE' && e.timestamp > fqEvent.timestamp)
    const afterT = (responseEvent || fqEvent).timestamp
    const nextSeg = (x.analytics.autoSegments || [])
      .filter(s => !s.isSystemEvent && (s.startTime + x.analytics.replayStartTime) > afterT)
      .sort((a, b) => a.startTime - b.startTime)[0]
    const id = nextSeg?.behavior?.label || 'unknown'
    firstBehaviorAfter[id] = (firstBehaviorAfter[id] || 0) + 1
  })
  const firstBehaviorSorted = Object.entries(firstBehaviorAfter).sort(([,a],[,b]) => b - a)

  // Test improvement after first query
  const testImprovements = perStudent.map(x => {
    const fqEvent = x.analytics.firstQueryEvent
    if (!fqEvent) return null
    const events = x.analytics.filteredEvents || []
    const testBefore = [...events].filter(e => e.type === 'TEST_CASE_RESULT' && e.timestamp < fqEvent.timestamp).pop()
    const testAfter  = events.find(e => e.type === 'TEST_CASE_RESULT' && e.timestamp > fqEvent.timestamp)
    if (!testBefore || !testAfter) return null
    return (testAfter.payload?.passed_count ?? 0) > (testBefore.payload?.passed_count ?? 0)
  }).filter(v => v != null)
  const improvedPct = testImprovements.length > 0
    ? Math.round(testImprovements.filter(Boolean).length / testImprovements.length * 100) : null

  // Time until next action after first query
  const timeToNextAction = perStudent.map(x => {
    const fqEvent = x.analytics.firstQueryEvent
    if (!fqEvent) return null
    const events = x.analytics.filteredEvents || []
    const next = events.find(e => e.timestamp > fqEvent.timestamp && e.type !== 'MOUSE_MOVE' && e.type !== 'TAB_STATE' && e.type !== 'CHAT_RESPONSE')
    return next ? (next.timestamp - fqEvent.timestamp) / 1000 : null
  }).filter(v => v != null)

  // ── Outcomes ──────────────────────────────────────────────────────────────
  const passRates = perStudent.map(x => {
    const tr = x.analytics.sessionStats?.full?.testResults
    if (!tr || !tr.lastTotalTests) return null
    return tr.lastPassedCount === tr.lastTotalTests ? 1 : 0
  }).filter(v => v != null)
  const passRate = passRates.length > 0 ? Math.round(avg(passRates) * 100) : null

  const durations = perStudent.map(x => x.analytics.replayDuration ? x.analytics.replayDuration / 1000 : null).filter(v => v != null)
  const queryTotals = perStudent.map(x => x.analytics.sessionStats?.full?.frequency?.chatQuery?.count ?? null).filter(v => v != null)

  // Behavior color map matching BEHAVIORAL_LEGEND
  const BEHAVIOR_COLOR_MAP = {
    implementing: '#22c55e',
    thinking:     '#3b82f6',
    seekingHelp:  '#a855f7',
    debugging:    '#ef4444',
    idle:         '#d1d5db',
    unknown:      '#d1d5db',
  }
  const getBehaviorColor = (id) => BEHAVIOR_COLOR_MAP[id] || '#d1d5db'

  const [openSections, setOpenSections] = useState({ distribution: true, before: true, query: true, after: true, effort: true, outcomes: true })
  const toggle = (key) => setOpenSections(s => ({ ...s, [key]: !s[key] }))

  return (
    <div style={{ overflowY: 'auto', flex: 1, padding: '2px 2px 24px' }}>

      {/* Distribution */}
      <SectionHeader title="Distribution" open={openSections.distribution} onToggle={() => toggle('distribution')} />
      {openSections.distribution && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '4px' }}>
          <StatBlock label="Students" value={n} sub={`of ${total} total`} />
          <StatBlock label="Share" value={`${pct}%`} sub="of class" />
          <StatBlock label="Group" value={groupLabel} />
        </div>
      )}

      {/* Before the query */}
      <SectionHeader title="Before the First Query" open={openSections.before} onToggle={() => toggle('before')} />
      {openSections.before && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '12px' }}>
            <StatBlock label="Avg Time Before Asking" value={fmtSec(avg(beforeTimes))} />
            <StatBlock label="Avg Runs Before" value={fmtNum(avg(beforeRuns), 1)} />
            <StatBlock label="Avg Errors Before" value={fmtNum(avg(beforeErrors), 1)} />
            <StatBlock label="Avg Tests Passed" value={fmtNum(avg(beforeTests), 1)} />
          </div>
          {behaviorBreakdown.length > 0 && (
            <div style={{ marginBottom: '16px', padding: '14px 16px', borderRadius: '8px', background: '#fafafa', border: '1px solid #f0f0f2' }}>
              <div style={{ fontSize: '11px', color: '#999', fontWeight: 500, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Behavioral Breakdown</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                {(() => {
                  const size = 120, cx = 60, cy = 60, r = 48
                  let cumAngle = -Math.PI / 2
                  const slices = behaviorBreakdown.map((b) => {
                    const angle = (b.pct / 100) * 2 * Math.PI
                    const x1 = cx + r * Math.cos(cumAngle)
                    const y1 = cy + r * Math.sin(cumAngle)
                    const x2 = cx + r * Math.cos(cumAngle + angle)
                    const y2 = cy + r * Math.sin(cumAngle + angle)
                    const large = angle > Math.PI ? 1 : 0
                    const path = b.pct >= 100
                      ? `M ${cx-r} ${cy} a ${r} ${r} 0 1 1 ${r*2} 0 a ${r} ${r} 0 1 1 -${r*2} 0`
                      : `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`
                    cumAngle += angle
                    return { path, color: getBehaviorColor(b.id), label: b.label, pct: b.pct }
                  })
                  return (
                    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
                      {slices.map((s, i) => (
                        <path key={i} d={s.path} fill={s.color} stroke="white" strokeWidth="1.5">
                          <title>{s.label}: {s.pct}%</title>
                        </path>
                      ))}
                      <circle cx={cx} cy={cy} r={26} fill="white" />
                    </svg>
                  )
                })()}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', flex: 1 }}>
                  {behaviorBreakdown.map((b) => (
                    <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '2px', flexShrink: 0, background: getBehaviorColor(b.id) }} />
                      <span style={{ fontSize: '12px', color: '#444', flex: 1 }}>{b.label}</span>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#111' }}>{b.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* The query */}
      <SectionHeader title="The Query" open={openSections.query} onToggle={() => toggle('query')} />
      {openSections.query && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', marginBottom: '12px' }}>
          <StatBlock label="Avg Query Length" value={avgQueryLen != null ? `${Math.round(avgQueryLen)} chars` : '—'} />
          <div style={{ padding: '14px 16px', borderRadius: '8px', background: '#fafafa', border: '1px solid #f0f0f2' }}>
            <div style={{ fontSize: '11px', color: '#999', fontWeight: 500, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Sample First Queries</div>
            {sampleQueries.length === 0
              ? <span style={{ fontSize: '12px', color: '#ccc' }}>No query text available</span>
              : sampleQueries.map((q, i) => (
                <div key={i} style={{ fontSize: '12px', color: '#444', padding: '5px 8px', background: 'white', borderRadius: '5px', border: '1px solid #eee', marginBottom: i < sampleQueries.length - 1 ? '5px' : 0 }}>
                  "{q}"
                </div>
              ))
            }
          </div>
        </div>
      )}

      {/* After the query */}
      <SectionHeader title="After the First Query" open={openSections.after} onToggle={() => toggle('after')} />
      {openSections.after && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '12px' }}>
            <div style={{ padding: '14px 16px', borderRadius: '8px', background: '#fafafa', border: '1px solid #f0f0f2' }}>
              <div style={{ fontSize: '11px', color: '#999', fontWeight: 500, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>First Behavior After</div>
              {firstBehaviorSorted.length === 0
                ? <span style={{ fontSize: '13px', color: '#ccc' }}>—</span>
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {firstBehaviorSorted.map(([label, count], i) => {
                      const rowPct = Math.round(count / n * 100)
                      return (
                        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: '#aaa', width: '16px', textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
                          <span style={{ fontSize: '12px', color: '#333', flex: 1 }}>{label}</span>
                          <div style={{ width: '60px', height: '4px', borderRadius: '2px', background: '#f0f0f2', overflow: 'hidden', flexShrink: 0 }}>
                            <div style={{ width: `${rowPct}%`, height: '100%', background: '#111', borderRadius: '2px' }} />
                          </div>
                          <span style={{ fontSize: '11px', color: '#888', width: '28px', textAlign: 'right', flexShrink: 0 }}>{rowPct}%</span>
                        </div>
                      )
                    })}
                  </div>
                )
              }
            </div>
            <StatBlock
              label="Test Improved After Query"
              value={improvedPct != null ? `${improvedPct}%` : '—'}
              sub={testImprovements.length > 0 ? `of ${testImprovements.length} students with data` : null}
            />
            <StatBlock
              label="Time to Next Action"
              value={fmtSec(avg(timeToNextAction))}
              sub="after first query"
            />
          </div>
        </>
      )}

      {/* Effort Progression */}
      <SectionHeader title="Effort Progression" open={openSections.effort} onToggle={() => toggle('effort')} />
      {openSections.effort && (() => {
        const PASSIVE_CATS = new Set(['ask-quick', 'ask-extended'])
        const ACTIVE_CATS  = new Set(['code-no-test', 'debugged-tested', 'debug-loop', 'ran-no-debug'])

        // Per-student effort scores across windows
        const studentEffort = perStudent.map(x => {
          const windows = getStudentQueryWindows(x.analytics)
          if (!windows || windows.length === 0) return null
          const wEvents = windows.map(w => ({
            ...w,
            events: (x.analytics.filteredEvents || []).filter(e => {
              const t = e.timestamp - x.analytics.replayStartTime
              return t >= w.startTime && t < w.endTime
            })
          }))
          const scores = wEvents.map(w => {
            const durS = (w.endTime - w.startTime) / 1000
            if (durS < 1) return null
            const edits  = w.events.filter(e => e.type === 'CODE_TYPE' || e.type === 'CODE_INDENT').length
            const runs   = w.events.filter(e => e.type === 'TERMINAL_RUN').length
            const errors = w.events.filter(e => e.type === 'TERMINAL_ERROR').length
            return {
              windowIndex: w.windowIndex,
              effort: edits / durS,
              activityRate: edits / durS,
              category: w.category,
            }
          }).filter(Boolean)
          // Passive ratio — across relevant windows
          const passiveCount = scores.filter(s => PASSIVE_CATS.has(s.category)).length
          const activeCount  = scores.filter(s => ACTIVE_CATS.has(s.category)).length
          const passiveRatio = scores.length > 0 ? passiveCount / scores.length : null

          // Trend: compare first half vs second half (only meaningful with 2+ windows)
          const mid = Math.ceil(scores.length / 2)
          const firstHalf  = scores.slice(0, mid)
          const secondHalf = scores.slice(mid)
          const avgFirst  = firstHalf.reduce((s, x) => s + x.effort, 0) / firstHalf.length
          const avgSecond = secondHalf.length > 0 ? secondHalf.reduce((s, x) => s + x.effort, 0) / secondHalf.length : avgFirst
          const trend = scores.length < 2 ? 'n/a' : avgSecond > avgFirst * 1.1 ? 'increasing' : avgSecond < avgFirst * 0.9 ? 'declining' : 'stable'

          return { scores, passiveRatio, passiveCount, activeCount, trend, avgFirst, avgSecond, n: scores.length }
        }).filter(Boolean)

        if (studentEffort.length === 0) return <div style={{ color: '#ccc', fontSize: '12px', paddingBottom: '12px' }}>Not enough window data</div>

        const trendCounts = { increasing: 0, declining: 0, stable: 0 }
        studentEffort.forEach(s => trendCounts[s.trend]++)

        const avgPassiveRatio = studentEffort.reduce((s, x) => s + x.passiveRatio, 0) / studentEffort.length
        const avgEffortFirst  = studentEffort.reduce((s, x) => s + x.avgFirst, 0) / studentEffort.length
        const avgEffortSecond = studentEffort.reduce((s, x) => s + x.avgSecond, 0) / studentEffort.length

        // Per-window-index avg effort across group
        const maxWindows = Math.max(...studentEffort.map(s => s.n))
        const byWindowIndex = []
        for (let i = 0; i < maxWindows; i++) {
          const vals = studentEffort.map(s => s.scores[i]?.effort).filter(v => v != null)
          byWindowIndex.push({ i, avg: vals.length > 0 ? vals.reduce((a,b)=>a+b,0)/vals.length : null, n: vals.length })
        }
        const maxEffort = Math.max(...byWindowIndex.map(w => w.avg || 0), 0.001)

        return (
          <div style={{ marginBottom: '12px' }}>
            {/* Avg effort per window index */}
            {byWindowIndex.length > 1 && (
              <div style={{ padding: '14px 16px', borderRadius: '8px', background: '#fafafa', border: '1px solid #f0f0f2' }}>
                <div style={{ fontSize: '11px', color: '#999', fontWeight: 500, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Avg Edits per Window</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '60px' }}>
                  {byWindowIndex.map((w, i) => {
                    const h = w.avg != null ? Math.max(4, Math.round((w.avg / maxEffort) * 56)) : 4
                    return (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        <div title={`Window ${i === 0 ? 'Pre-query' : `Q${i}`}: ${w.avg?.toFixed(2) ?? '—'} (n=${w.n})`}
                          style={{ width: '100%', height: `${h}px`, borderRadius: '3px 3px 0 0', background: i === 0 ? '#3b82f6' : '#111', opacity: w.n < 2 ? 0.3 : 1 }} />
                        <span style={{ fontSize: '9px', color: '#aaa' }}>{i === 0 ? 'pre' : `Q${i}`}</span>
                      </div>
                    )
                  })}
                </div>
                <div style={{ fontSize: '10px', color: '#ccc', marginTop: '6px' }}>Blue = pre-query window. Bars with &lt;2 students are faded.</div>
              </div>
            )}
          </div>
        )
      })()}

      {/* Outcomes */}
      <SectionHeader title="Outcomes" open={openSections.outcomes} onToggle={() => toggle('outcomes')} />
      {openSections.outcomes && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
          <StatBlock label="Pass Rate" value={passRate != null ? `${passRate}%` : '—'} sub={`${passRates.filter(Boolean).length} of ${passRates.length} students`} />
          <StatBlock label="Avg Session Duration" value={fmtSec(avg(durations))} />
          <StatBlock label="Avg Queries / Session" value={fmtNum(avg(queryTotals), 1)} />
        </div>
      )}

    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ClassOverview({ students = [], focusRequest = null, onFocusHandled }) {
  const { sessionData, sessionId, isGui } = useContext(SessionDataContext)
  const { stats, getEventsForUser, loading: telemetryLoading } = useContext(TelemetryDataContext)
  const { analyticsById, classAggregates } = useContext(StudentAnalyticsContext)

  const [showStudents, setShowStudents]     = useState(false)
  const [viewMode, setViewMode]             = useState('list')
  const [sequenceMode, setSequenceMode]     = useState('behavior')  // 'behavior' | 'windows'
  const [taxonomyFilter, setTaxonomyFilter] = useState('all')
  const [searchQuery, setSearchQuery]       = useState('')
  const [studentFilter, setStudentFilter]   = useState('all')
  const [resultFilter, setResultFilter]     = useState('all')
  const [chatFilter, setChatFilter]         = useState('all')
  const [timeFilter, setTimeFilter]         = useState('all')
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [detailTab, setDetailTab]           = useState('overview')
  const [sequencesTab, setSequencesTab]     = useState('students')  // 'students' | 'analytics' | 'charts'

  const handleStudentClick = (student) => {
    if (!sessionId || !student.id) { console.warn('Missing session ID or student ID — skipping'); return }
    setSelectedStudent(student)
    setDetailTab('overview')
  }

  // A page embedding the Monitor asked for one student (InstructorDashboardPanel's
  // focusRequest): open their detail once they are in the list, once per request.
  const handledFocusRef = useRef(null)
  useEffect(() => {
    if (!focusRequest?.studentId || handledFocusRef.current === focusRequest) return
    const match = students.find((s) => String(s.id) === String(focusRequest.studentId))
    if (!match) return
    handledFocusRef.current = focusRequest
    handleStudentClick(match)
    onFocusHandled?.()
  }, [focusRequest, students]) // eslint-disable-line react-hooks/exhaustive-deps

  const formatDuration = (seconds) => {
    if (!seconds) return '—'
    const m = Math.floor(seconds / 60), s = seconds % 60
    return `${m}m ${s}s`
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' +
      date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  }

  const enrichedStudents = useMemo(() => students.map(student => {
    const summary = stats?.studentSummaries?.[student.id] || {}
    return { ...student, duration: summary.duration ?? student.duration, chat_count: summary.chatCount ?? student.chat_count, tests_passed: summary.testsPassed ?? student.tests_passed, tests_total: summary.testsTotal ?? student.tests_total, last_active: summary.lastActive ?? student.last_active }
  }), [students, stats])

  const filteredStudents = useMemo(() => {
    let result = [...enrichedStudents]
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(s => (s.first_name?.toLowerCase().includes(q)) || (s.last_name?.toLowerCase().includes(q)) || (s.username?.toLowerCase().includes(q)) || (s.display_name?.toLowerCase().includes(q)))
    }
    if (resultFilter === 'passed') result = result.filter(s => s.tests_passed === s.tests_total && s.tests_total > 0)
    else if (resultFilter === 'failed') result = result.filter(s => s.tests_passed < s.tests_total || s.tests_total === 0)
    if (chatFilter === 'used') result = result.filter(s => s.chat_count != null && s.chat_count > 0)
    else if (chatFilter === 'none') result = result.filter(s => !s.chat_count || s.chat_count === 0)
    else if (chatFilter === 'high') result = result.filter(s => s.chat_count != null && s.chat_count >= 10)
    if (timeFilter === 'today') { const today = new Date().toDateString(); result = result.filter(s => s.last_active && new Date(s.last_active).toDateString() === today) }
    else if (timeFilter === 'week') { const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); result = result.filter(s => s.last_active && new Date(s.last_active) >= weekAgo) }

    // Taxonomy filter — student appears in a group if any of their windows match
    if (taxonomyFilter !== 'all') {
      result = result.filter(student => {
        const analytics = analyticsById[student.id]?.analytics || analyticsById[String(student.id)]?.analytics || null
        return getStudentCategories(analytics, sequenceMode).includes(taxonomyFilter)
      })
    }

    return result
  }, [enrichedStudents, searchQuery, studentFilter, resultFilter, chatFilter, timeFilter, taxonomyFilter, sequenceMode, viewMode, analyticsById])


  // ── Export ────────────────────────────────────────────────────────────────
  const exportAllGroupData = () => {
    const avgFn   = (arr) => arr.length > 0 ? arr.reduce((a,b)=>a+b,0)/arr.length : null
    const fmtNum  = (n, dec=1) => n==null ? '' : Number.isInteger(n) ? String(n) : Number(n).toFixed(dec)
    const esc     = (v) => `"${String(v??'').replace(/"/g,'""')}"`
    const download = (filename, csv) => {
      const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' })
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href=url; a.download=filename; a.click(); URL.revokeObjectURL(url)
    }
    const ts = new Date().toISOString().slice(0,10)
    const MODES = ['behavior','pre-query']
    const ALL_GROUP_KEYS = ['all',...Object.keys(TAXONOMY)]
    const PASSIVE_CATS = new Set(['ask-quick','ask-extended'])

    // Helper: compute all analytics stats for a set of students under a given mode
    const computeGroupStats = (gStudents, mode) => {
      const per = gStudents.map(s => {
        const a = analyticsById[s.id]?.analytics || analyticsById[String(s.id)]?.analytics || null
        return a ? {student:s, analytics:a} : null
      }).filter(Boolean)

      const beforeTimes  = per.map(x=>x.analytics.firstQueryMs!=null?x.analytics.firstQueryMs/1000:null).filter(v=>v!=null)
      const beforeRuns   = per.map(x=>{const fq=x.analytics.firstQueryMs;if(fq==null)return null;return(x.analytics.filteredEvents||[]).filter(e=>e.type==='TERMINAL_RUN'&&(e.timestamp-x.analytics.replayStartTime)<fq).length}).filter(v=>v!=null)
      const beforeErrors = per.map(x=>{const fq=x.analytics.firstQueryMs;if(fq==null)return null;return(x.analytics.filteredEvents||[]).filter(e=>e.type==='TERMINAL_ERROR'&&(e.timestamp-x.analytics.replayStartTime)<fq).length}).filter(v=>v!=null)
      const beforeTests  = per.map(x=>{const fq=x.analytics.firstQueryMs;if(fq==null)return null;const te=(x.analytics.filteredEvents||[]).filter(e=>e.type==='TEST_CASE_RESULT'&&(e.timestamp-x.analytics.replayStartTime)<fq);return te.length>0?te[te.length-1].payload?.passed_count??null:null}).filter(v=>v!=null)
      const queryLens    = per.map(x=>{const fq=x.analytics.firstQueryEvent;if(!fq)return null;return(fq.payload?.text||fq.payload?.message||fq.payload?.content||'').length}).filter(v=>v!=null)

      const firstBehAfter = {}
      per.forEach(x=>{
        const fqEvent=x.analytics.firstQueryEvent; if(!fqEvent)return
        const events=x.analytics.filteredEvents||[]
        const resp=events.find(e=>e.type==='CHAT_RESPONSE'&&e.timestamp>fqEvent.timestamp)
        const afterT=(resp||fqEvent).timestamp
        const nextSeg=(x.analytics.autoSegments||[]).filter(s=>!s.isSystemEvent&&(s.startTime+x.analytics.replayStartTime)>afterT).sort((a,b)=>a.startTime-b.startTime)[0]
        const lbl=nextSeg?.behavior?.label||'unknown'
        firstBehAfter[lbl]=(firstBehAfter[lbl]||0)+1
      })
      const topBeh = Object.entries(firstBehAfter).sort(([,a],[,b])=>b-a)[0]

      const testImprove = per.map(x=>{const fqEvent=x.analytics.firstQueryEvent;if(!fqEvent)return null;const events=x.analytics.filteredEvents||[];const tb=[...events].filter(e=>e.type==='TEST_CASE_RESULT'&&e.timestamp<fqEvent.timestamp).pop();const ta=events.find(e=>e.type==='TEST_CASE_RESULT'&&e.timestamp>fqEvent.timestamp);if(!tb||!ta)return null;return(ta.payload?.passed_count??0)>(tb.payload?.passed_count??0)}).filter(v=>v!=null)
      const improvedPct = testImprove.length>0?Math.round(testImprove.filter(Boolean).length/testImprove.length*100):null
      const timeToNext  = per.map(x=>{const fqEvent=x.analytics.firstQueryEvent;if(!fqEvent)return null;const events=x.analytics.filteredEvents||[];const next=events.find(e=>e.timestamp>fqEvent.timestamp&&e.type!=='MOUSE_MOVE'&&e.type!=='TAB_STATE'&&e.type!=='CHAT_RESPONSE');return next?(next.timestamp-fqEvent.timestamp)/1000:null}).filter(v=>v!=null)
      const passRates   = per.map(x=>{const tr=x.analytics.sessionStats?.full?.testResults;if(!tr||!tr.lastTotalTests)return null;return tr.lastPassedCount===tr.lastTotalTests?1:0}).filter(v=>v!=null)
      const durations   = per.map(x=>x.analytics.replayDuration?x.analytics.replayDuration/1000:null).filter(v=>v!=null)
      const queryTotals = per.map(x=>x.analytics.sessionStats?.full?.frequency?.chatQuery?.count??null).filter(v=>v!=null)

      // Effort metrics
      const effortData = per.map(x=>{
        const allWindows=getStudentQueryWindows(x.analytics)
        if(!allWindows||allWindows.length===0)return null
        const windows=mode==='pre-query'?allWindows.slice(0,1):allWindows
        const scores=windows.map(w=>{
          const durS=(w.endTime-w.startTime)/1000; if(durS<1)return null
          const wEvts=(x.analytics.filteredEvents||[]).filter(e=>{const t=e.timestamp-x.analytics.replayStartTime;return t>=w.startTime&&t<w.endTime})
          const edits=wEvts.filter(e=>e.type==='CODE_TYPE'||e.type==='CODE_INDENT').length
          const runs=wEvts.filter(e=>e.type==='TERMINAL_RUN').length
          const errors=wEvts.filter(e=>e.type==='TERMINAL_ERROR').length
          return{effort:edits,category:w.category}
        }).filter(Boolean)
        if(scores.length<1)return null
        const passiveCount=scores.filter(s=>PASSIVE_CATS.has(s.category)).length
        const mid=Math.ceil(scores.length/2)
        const avgFirst=avgFn(scores.slice(0,mid).map(s=>s.effort))
        const avgSecond=avgFn(scores.slice(mid).map(s=>s.effort))
        const trend=avgSecond>avgFirst*1.1?'increasing':avgSecond<avgFirst*0.9?'declining':'stable'
        return{passiveRatio:passiveCount/scores.length,trend,avgFirst,avgSecond}
      }).filter(Boolean)
      const trendCounts={increasing:0,declining:0,stable:0}
      effortData.forEach(e=>trendCounts[e.trend]++)

      return {
        n: per.length,
        avg_time_before_s: fmtNum(avgFn(beforeTimes),1),
        avg_runs_before: fmtNum(avgFn(beforeRuns),1),
        avg_errors_before: fmtNum(avgFn(beforeErrors),1),
        avg_tests_passed_before: fmtNum(avgFn(beforeTests),1),
        avg_query_length_chars: fmtNum(avgFn(queryLens),0),
        top_first_behavior: topBeh?.[0]||'',
        top_first_behavior_pct: topBeh&&per.length>0?fmtNum(topBeh[1]/per.length*100,1):'',
        pct_test_improved: improvedPct??'',
        avg_time_to_next_action_s: fmtNum(avgFn(timeToNext),1),
        pass_rate_pct: passRates.length>0?fmtNum(avgFn(passRates)*100,1):'',
        avg_session_duration_s: fmtNum(avgFn(durations),1),
        avg_queries_per_session: fmtNum(avgFn(queryTotals),1),
        avg_passive_ratio: effortData.length>0?fmtNum(avgFn(effortData.map(e=>e.passiveRatio))*100,1):'',
        trend_increasing: trendCounts.increasing,
        trend_stable: trendCounts.stable,
        trend_declining: trendCounts.declining,
        avg_activity_rate_first_half: fmtNum(avgFn(effortData.map(e=>e.avgFirst)),3),
        avg_activity_rate_second_half: fmtNum(avgFn(effortData.map(e=>e.avgSecond)),3),
      }
    }

    // ── FILE 1: Group summary — all groups × both modes ───────────────────
    const summaryHeaders = [
      'mode','group_id','group_label','n_students','pct_of_class',
      'avg_time_before_query_s','avg_runs_before','avg_errors_before','avg_tests_passed_before',
      'avg_query_length_chars',
      'top_first_behavior_after','top_first_behavior_pct',
      'pct_test_improved_after_query','avg_time_to_next_action_s',
      'pass_rate_pct','avg_session_duration_s','avg_queries_per_session',
      'avg_passive_ratio_pct','trend_increasing','trend_stable','trend_declining',
      'avg_activity_rate_first_half','avg_activity_rate_second_half',
    ]
    const summaryRows = [summaryHeaders.join(',')]

    MODES.forEach(mode => {
      ALL_GROUP_KEYS.forEach(gKey => {
        const gStudents = gKey==='all'
          ? enrichedStudents
          : enrichedStudents.filter(s=>{
              const a=analyticsById[s.id]?.analytics||analyticsById[String(s.id)]?.analytics||null
              return getStudentCategories(a,mode).includes(gKey)
            })
        if(gStudents.length===0)return
        const stats=computeGroupStats(gStudents,mode)
        const pct=enrichedStudents.length>0?fmtNum(gStudents.length/enrichedStudents.length*100,1):''
        summaryRows.push([
          esc(mode), esc(gKey), esc(gKey==='all'?'All Students':TAXONOMY[gKey]?.label||gKey),
          stats.n, pct,
          stats.avg_time_before_s, stats.avg_runs_before, stats.avg_errors_before, stats.avg_tests_passed_before,
          stats.avg_query_length_chars,
          esc(stats.top_first_behavior), stats.top_first_behavior_pct,
          stats.pct_test_improved, stats.avg_time_to_next_action_s,
          stats.pass_rate_pct, stats.avg_session_duration_s, stats.avg_queries_per_session,
          stats.avg_passive_ratio, stats.trend_increasing, stats.trend_stable, stats.trend_declining,
          stats.avg_activity_rate_first_half, stats.avg_activity_rate_second_half,
        ].join(','))
      })
    })

    // ── FILE 2: Student master — one row per student, both mode memberships ──
    const studentHeaders = [
      'student_id','student_name',
      'fullsession_group_ids','fullsession_group_labels',
      'prequery_group_ids','prequery_group_labels',
      'session_duration_s','total_queries',
      'tests_passed','tests_total','passed_all',
      'time_before_first_query_s','runs_before_query','errors_before_query',
      'edits_before_query',
      'avg_effort_score','passive_ratio_pct','effort_trend',
      'first_query_text_sample',
    ]
    const studentRows = [studentHeaders.join(',')]

    enrichedStudents.forEach(student => {
      const a=analyticsById[student.id]?.analytics||analyticsById[String(student.id)]?.analytics||null
      if(!a)return
      const name=student.display_name||student.username||`${student.first_name||''} ${student.last_name||''}`.trim()||'Unknown'
      const catsFS=getStudentCategories(a,'behavior')
      const catsPQ=getStudentCategories(a,'pre-query')
      const fqMs=a.firstQueryMs
      const dur=a.replayDuration?a.replayDuration/1000:''
      const qTotal=a.sessionStats?.full?.frequency?.chatQuery?.count??''
      const tr=a.sessionStats?.full?.testResults
      const passedAll=(tr?.lastTotalTests>0&&tr?.lastPassedCount===tr?.lastTotalTests)?1:0
      const beforeRuns=fqMs!=null?(a.filteredEvents||[]).filter(e=>e.type==='TERMINAL_RUN'&&(e.timestamp-a.replayStartTime)<fqMs).length:''
      const beforeErrors=fqMs!=null?(a.filteredEvents||[]).filter(e=>e.type==='TERMINAL_ERROR'&&(e.timestamp-a.replayStartTime)<fqMs).length:''
      const beforeEdits=fqMs!=null?(a.filteredEvents||[]).filter(e=>(e.type==='CODE_TYPE'||e.type==='CODE_INDENT')&&(e.timestamp-a.replayStartTime)<fqMs).length:''
      const timeBeforeQ=fqMs!=null?(fqMs/1000).toFixed(1):''
      const firstQueryText=(a.firstQueryEvent?.payload?.text||a.firstQueryEvent?.payload?.message||a.firstQueryEvent?.payload?.content||'').slice(0,120)

      // Effort
      const allWins=getStudentQueryWindows(a)
      // compute effort for full session and pre-query separately
      const computeEffortScores=(wins)=>wins.map(w=>{
        const durS=(w.endTime-w.startTime)/1000; if(durS<1)return null
        const wEvts=(a.filteredEvents||[]).filter(e=>{const t=e.timestamp-a.replayStartTime;return t>=w.startTime&&t<w.endTime})
        const edits=wEvts.filter(e=>e.type==='CODE_TYPE'||e.type==='CODE_INDENT').length
        return{effort:edits/durS,category:w.category}
      }).filter(Boolean)
      let avgEffort='', passiveRatio='', effortTrend=''
      if(allWins.length>=1){
        const scores=allWins.map(w=>{
          const durS=(w.endTime-w.startTime)/1000; if(durS<1)return null
          const wEvts=(a.filteredEvents||[]).filter(e=>{const t=e.timestamp-a.replayStartTime;return t>=w.startTime&&t<w.endTime})
          const edits=wEvts.filter(e=>e.type==='CODE_TYPE'||e.type==='CODE_INDENT').length
          const runs=wEvts.filter(e=>e.type==='TERMINAL_RUN').length
          const errors=wEvts.filter(e=>e.type==='TERMINAL_ERROR').length
          return{effort:edits,category:w.category}
        }).filter(Boolean)
        if(scores.length>=2){
          const passiveCount=scores.filter(s=>PASSIVE_CATS.has(s.category)).length
          const mid=Math.ceil(scores.length/2)
          const af=avgFn(scores.slice(0,mid).map(s=>s.effort))
          const as2=avgFn(scores.slice(mid).map(s=>s.effort))
          avgEffort=fmtNum(avgFn(scores.map(s=>s.effort)),3)
          passiveRatio=fmtNum(passiveCount/scores.length*100,1)
          effortTrend=as2>af*1.1?'increasing':as2<af*0.9?'declining':'stable'
        }
      }

      studentRows.push([
        esc(student.id), esc(name),
        esc(catsFS.join('|')), esc(catsFS.map(c=>TAXONOMY[c]?.label||c).join('|')),
        esc(catsPQ.join('|')), esc(catsPQ.map(c=>TAXONOMY[c]?.label||c).join('|')),
        dur, qTotal,
        tr?.lastPassedCount??'', tr?.lastTotalTests??'', passedAll,
        timeBeforeQ, beforeRuns, beforeErrors, beforeEdits,
        avgEffort, passiveRatio, esc(effortTrend),
        esc(firstQueryText),
      ].join(','))
    })

    // ── FILE 3: Behavior segments — one row per segment per student ────────
    const segHeaders = [
      'student_id','student_name',
      'fullsession_group_ids','prequery_group_ids',
      'session_duration_s','total_queries','tests_passed','tests_total','passed_all',
      'time_before_first_query_s',
      'segment_index','behavior_id','behavior_label',
      'start_s','end_s','duration_s','is_before_query',
    ]
    const segRows = [segHeaders.join(',')]

    enrichedStudents.forEach(student => {
      const a=analyticsById[student.id]?.analytics||analyticsById[String(student.id)]?.analytics||null
      if(!a)return
      const name=student.display_name||student.username||`${student.first_name||''} ${student.last_name||''}`.trim()||'Unknown'
      const catsFS=getStudentCategories(a,'behavior').join('|')
      const catsPQ=getStudentCategories(a,'pre-query').join('|')
      const fqMs=a.firstQueryMs
      const dur=a.replayDuration?a.replayDuration/1000:''
      const qTotal=a.sessionStats?.full?.frequency?.chatQuery?.count??''
      const tr=a.sessionStats?.full?.testResults
      const passedAll=(tr?.lastTotalTests>0&&tr?.lastPassedCount===tr?.lastTotalTests)?1:0
      const timeBeforeQ=fqMs!=null?(fqMs/1000).toFixed(1):''
      // visibleSegments has behavior populated; fall back to autoSegments filtered to those with behavior
      const rawSegs = a.visibleSegments || a.autoSegments || []
      const segs = rawSegs.filter(s => !s.isSystemEvent && s.behavior)
      if(segs.length===0){
        segRows.push([esc(student.id),esc(name),esc(catsFS),esc(catsPQ),dur,qTotal,tr?.lastPassedCount??'',tr?.lastTotalTests??'',passedAll,timeBeforeQ,'','','','','','',''].join(','))
      } else {
        segs.forEach((seg,idx)=>{
          segRows.push([
            esc(student.id),esc(name),esc(catsFS),esc(catsPQ),
            dur,qTotal,tr?.lastPassedCount??'',tr?.lastTotalTests??'',passedAll,timeBeforeQ,
            idx,esc(seg.behavior?.id||'unknown'),esc(seg.behavior?.label||'Unknown'),
            (seg.startTime/1000).toFixed(2),(seg.endTime/1000).toFixed(2),((seg.endTime-seg.startTime)/1000).toFixed(2),
            fqMs!=null?(seg.startTime<fqMs?1:0):'',
          ].join(','))
        })
      }
    })

    // ── FILE 4: Query windows — one row per window per student ─────────────
    const winHeaders = [
      'student_id','student_name',
      'fullsession_group_ids','prequery_group_ids',
      'window_index','is_pre_query','window_category','window_label',
      'start_s','end_s','duration_s',
      'runs','errors','edits',
      'total_edits',
    ]
    const winRows = [winHeaders.join(',')]

    enrichedStudents.forEach(student => {
      const a=analyticsById[student.id]?.analytics||analyticsById[String(student.id)]?.analytics||null
      if(!a)return
      const name=student.display_name||student.username||`${student.first_name||''} ${student.last_name||''}`.trim()||'Unknown'
      const catsFS=getStudentCategories(a,'behavior').join('|')
      const catsPQ=getStudentCategories(a,'pre-query').join('|')
      const windows=getStudentQueryWindows(a)
      windows.forEach(w=>{
        const durS=(w.endTime-w.startTime)/1000
        const wEvts=(a.filteredEvents||[]).filter(e=>{const t=e.timestamp-a.replayStartTime;return t>=w.startTime&&t<w.endTime})
        const runs=wEvts.filter(e=>e.type==='TERMINAL_RUN').length
        const errors=wEvts.filter(e=>e.type==='TERMINAL_ERROR').length
        const edits=wEvts.filter(e=>e.type==='CODE_TYPE'||e.type==='CODE_INDENT').length
        const effort=durS>=1?fmtNum((edits/durS),3):''
        const actRate=fmtNum(edits,0)
        winRows.push([
          esc(student.id),esc(name),esc(catsFS),esc(catsPQ),
          w.windowIndex,w.isPreQuery?1:0,esc(w.category),esc(WINDOW_TAXONOMY[w.category]?.label||w.category),
          (w.startTime/1000).toFixed(2),(w.endTime/1000).toFixed(2),durS.toFixed(2),
          runs,errors,edits,actRate,
        ].join(','))
      })
    })

    // ── Combine into single file with section headers ───────────────────
    const combined = [
      '### GROUP SUMMARY (all groups x both modes) ###',
      summaryRows.join('\n'),
      '',
      '### STUDENT MASTER (one row per student) ###',
      studentRows.join('\n'),
      '',
      '### BEHAVIOR SEGMENTS (one row per segment per student) ###',
      segRows.join('\n'),
      '',
      '### QUERY WINDOWS (one row per window per student) ###',
      winRows.join('\n'),
    ].join('\n')
    download(`vizpi_export_${ts}.csv`, combined)
  }

  const getDisplayName = (student) => student.display_name || student.username || `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Unknown'

  const closeModal = () => {
    setShowStudents(false)
    setSearchQuery(''); setStudentFilter('all'); setResultFilter('all'); setChatFilter('all'); setTimeFilter('all')
  }

  const selectStyle = {
    padding: '7px 28px 7px 14px', borderRadius: '20px', border: '1px solid #e0e0e0', background: '#f7f7f8',
    fontSize: '13px', color: '#444', cursor: 'pointer', outline: 'none', appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23666' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
  }

  return (
    <div className="class-overview">
      <h2>Class Overview</h2>
      <div className="overview-stats">
        <div className="stat-item">
          <span className="stat-label">Total Students:</span>
          <span className="stat-value">{students.length || '—'}</span>
        </div>
      </div>
      <button className="see-all-button" onClick={() => setShowStudents(true)}>See All Students</button>

      {/* ── Student List Modal ─────────────────────────────────────────── */}
      {showStudents && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '28px', maxWidth: '1100px', width: '90%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#111' }}>Students</h2>
                <span style={{ fontSize: '13px', color: '#888', marginTop: '2px', display: 'block' }}>{filteredStudents.length} of {students.length} students</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <ViewToggle value={viewMode} onChange={(v) => { setViewMode(v); setTaxonomyFilter('all') }} opts={[{ key: 'list', label: 'List' }, { key: 'sequences', label: 'Sequences' }]} />
                <button onClick={closeModal} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666', padding: '4px 8px', lineHeight: 1 }}>✕</button>
              </div>
            </div>

            {/* Search bar */}
            <div style={{ display: 'flex', alignItems: 'center', border: '2px solid #d0d5dd', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', transition: 'border-color 0.2s' }}
              onFocus={e => e.currentTarget.style.borderColor = '#5ba0d6'}
              onBlur={e => e.currentTarget.style.borderColor = '#d0d5dd'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '10px', flexShrink: 0 }}>
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input type="text" placeholder="Search student..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                style={{ border: 'none', outline: 'none', fontSize: '14px', width: '100%', color: '#333', background: 'transparent' }} />
            </div>

            {/* Filter dropdowns */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap' }}>
              <select value={studentFilter} onChange={e => setStudentFilter(e.target.value)} style={selectStyle}>
                <option value="all">All Students</option>
              </select>
              <select value={resultFilter} onChange={e => setResultFilter(e.target.value)} style={selectStyle}>
                <option value="all">All Results</option>
                <option value="passed">All Passed</option>
                <option value="failed">Has Failures</option>
              </select>
              <select value={chatFilter} onChange={e => setChatFilter(e.target.value)} style={selectStyle}>
                <option value="all">All LLM Usage</option>
                <option value="used">Used LLM</option>
                <option value="none">No LLM</option>
                <option value="high">10+ Messages</option>
              </select>
              <select value={timeFilter} onChange={e => setTimeFilter(e.target.value)} style={selectStyle}>
                <option value="all">Any Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
              </select>
            </div>

            {telemetryLoading && <div style={{ textAlign: 'center', color: '#999', padding: '12px 0', fontSize: '13px' }}>Loading telemetry data...</div>}

            {/* ── LIST VIEW ─────────────────────────────────────────────── */}
            {viewMode === 'list' && (
              <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                {filteredStudents.length === 0 && !telemetryLoading && <div style={{ textAlign: 'center', color: '#999', padding: '32px 0', fontSize: '14px' }}>No students found</div>}
                {filteredStudents.map(student => (
                  <div key={student.id} onClick={() => handleStudentClick(student)}
                    style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', border: '1px solid #e8e8eb', borderRadius: '10px', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s', background: 'white' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f0f6ff'; e.currentTarget.style.borderColor = '#a8c8e8' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e8e8eb' }}
                  >
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#f0f0f2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '14px', flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getDisplayName(student)}</div>
                      {student.duration != null && (
                        <div style={{ fontSize: '12px', color: '#888', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                          {formatDuration(student.duration)}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0, fontSize: '13px', color: '#666' }}>
                      {student.chat_count != null && student.chat_count > 0 && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                          {student.chat_count}
                        </span>
                      )}
                      {student.tests_total != null && <span>Tests: {student.tests_passed ?? 0}/{student.tests_total}</span>}
                      {student.last_active && <span style={{ color: '#999' }}>{formatDate(student.last_active)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── SEQUENCES VIEW ────────────────────────────────────────── */}
            {viewMode === 'sequences' && (
              <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>

                {/* Sequences top bar — pills + export */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '14px' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flex: 1 }}>
                  {/* All pill */}
                  <button onClick={() => setTaxonomyFilter('all')} style={{
                    padding: '5px 12px', borderRadius: '20px', border: '1px solid',
                    borderColor: taxonomyFilter === 'all' ? '#555' : '#e0e0e0',
                    background: taxonomyFilter === 'all' ? '#111' : 'white',
                    color: taxonomyFilter === 'all' ? 'white' : '#555',
                    fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '5px',
                  }}>
                    All
                    <span style={{
                      fontSize: '11px', fontWeight: 600,
                      background: taxonomyFilter === 'all' ? 'rgba(255,255,255,0.2)' : '#f0f0f2',
                      color: taxonomyFilter === 'all' ? 'white' : '#888',
                      borderRadius: '10px', padding: '0 6px',
                    }}>{sequenceMode === 'behavior' ? enrichedStudents.length : enrichedStudents.filter(s => {
                      const a = analyticsById[s.id]?.analytics || analyticsById[String(s.id)]?.analytics || null
                      return (a?.filteredEvents || []).some(e => e.type === 'CHAT_QUERY')
                    }).length}</span>
                  </button>

                  {Object.entries(TAXONOMY).map(([key, tax]) => {
                    const count = enrichedStudents.filter(s => {
                      const a = analyticsById[s.id]?.analytics || analyticsById[String(s.id)]?.analytics || null
                      return getStudentCategories(a, sequenceMode).includes(key)
                    }).length
                    const alwaysShow = sequenceMode === 'pre-query' && (key === 'ask-quick' || key === 'ask-extended')
                    if (count === 0 && !alwaysShow) return null
                    const active = taxonomyFilter === key
                    return (
                      <button key={key} onClick={() => setTaxonomyFilter(active ? 'all' : key)} style={{
                        padding: '5px 12px', borderRadius: '20px', border: '1px solid',
                        borderColor: active ? '#111' : '#e0e0e0',
                        background: active ? '#111' : 'white',
                        color: active ? 'white' : '#555',
                        fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '5px',
                        transition: 'all 0.15s',
                      }}>
                        {tax.label}
                        <span style={{
                          fontSize: '11px', fontWeight: 600,
                          background: active ? 'rgba(255,255,255,0.15)' : '#f0f0f2',
                          color: active ? 'white' : '#888',
                          borderRadius: '10px', padding: '0 6px',
                        }}>{count}</span>
                      </button>
                    )
                  })}
                </div>
                 <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    {/* Export CSVs */}
                    <button onClick={exportAllGroupData} title="Export group analytics + sequences as CSV" style={{
                      display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px',
                      borderRadius: '8px', border: '1px solid #e0e0e0', background: 'white',
                      fontSize: '12px', fontWeight: 500, color: '#444', cursor: 'pointer',
                      whiteSpace: 'nowrap', transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f7'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Export CSVs
                    </button>

                    {/* Download All Telemetry */}
                    <button onClick={() => {
                      const allTelemetry = {}
                      enrichedStudents.forEach(student => {
                        const events = getEventsForUser(student.id)
                        if (events && events.length > 0) {
                          allTelemetry[student.id] = {
                            student_id: student.id,
                            name: student.display_name || student.username || `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Unknown',
                            session_id: sessionId,
                            events
                          }
                        }
                      })
                      const blob = new Blob([JSON.stringify(allTelemetry, null, 2)], { type: 'application/json' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `telemetry_all_session_${sessionId}_${new Date().toISOString().slice(0,10)}.json`
                      a.click()
                      URL.revokeObjectURL(url)
                    }} title="Download all students' raw telemetry as JSON" style={{
                      display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px',
                      borderRadius: '8px', border: '1px solid #e0e0e0', background: 'white',
                      fontSize: '12px', fontWeight: 500, color: '#444', cursor: 'pointer',
                      whiteSpace: 'nowrap', transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f7'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/>
                      </svg>
                      All Telemetry
                    </button>

                    {/* Download Random Student */}
                    <button onClick={() => {
                      const withEvents = enrichedStudents.filter(s => {
                        const events = getEventsForUser(s.id)
                        return events && events.length > 0
                      })
                      if (withEvents.length === 0) { alert('No telemetry data available'); return }
                      const random = withEvents[Math.floor(Math.random() * withEvents.length)]
                      const events = getEventsForUser(random.id)
                      const name = random.display_name || random.username || `${random.first_name || ''} ${random.last_name || ''}`.trim() || 'Unknown'
                      const data = {
                        student_id: random.id,
                        name,
                        session_id: sessionId,
                        events
                      }
                      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `telemetry_student_${random.id}_session_${sessionId}.json`
                      a.click()
                      URL.revokeObjectURL(url)
                      alert(`Downloaded telemetry for: ${name} (ID: ${random.id})`)
                    }} title="Download one random student's telemetry as JSON" style={{
                      display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px',
                      borderRadius: '8px', border: '1px solid #e0e0e0', background: 'white',
                      fontSize: '12px', fontWeight: 500, color: '#444', cursor: 'pointer',
                      whiteSpace: 'nowrap', transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f7'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                      </svg>
                      Random Student
                    </button>
                  </div>
                </div>

                {/* Behavior / Pre-Query toggle + Students / Analytics sub-tab */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <ViewToggle
                    value={sequenceMode}
                    onChange={(v) => { setSequenceMode(v); setTaxonomyFilter('all') }}
                    opts={[{ key: 'behavior', label: 'Full Session' }, { key: 'pre-query', label: 'Pre-Query' }]}
                  />
                  <ViewToggle
                    value={sequencesTab}
                    onChange={setSequencesTab}
                    opts={[{ key: 'students', label: 'Students' }, { key: 'analytics', label: 'Analytics' }, { key: 'charts', label: 'Charts' }]}
                  />
                </div>

                {sequencesTab === 'charts' ? (
                  <GroupCharts
                    allStudents={enrichedStudents}
                    analyticsById={analyticsById}
                    sequenceMode={sequenceMode}
                  />
                ) : sequencesTab === 'analytics' ? (
                  <GroupAnalytics
                    groupStudents={filteredStudents}
                    allStudents={enrichedStudents}
                    taxonomyFilter={taxonomyFilter}
                    sequenceMode={sequenceMode}
                    analyticsById={analyticsById}
                    TAXONOMY={TAXONOMY}
                  />
                ) : (
                  <>
                    <SequenceLegend mode={sequenceMode} />
                    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr auto', gap: '14px', padding: '0 14px 8px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Student</span>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {sequenceMode === 'pre-query' ? 'Before First Query' : 'Session Sequence'}
                      </span>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stats</span>
                    </div>

                    {filteredStudents.length === 0 && !telemetryLoading && (
                      <div style={{ textAlign: 'center', color: '#999', padding: '32px 0', fontSize: '14px' }}>No students found</div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {filteredStudents.map(student => {
                        const studentAnalytics = analyticsById[student.id]?.analytics || analyticsById[String(student.id)]?.analytics || null
                        const segments = studentAnalytics?.autoSegments?.filter(s => !s.isSystemEvent) || []
                        const totalDuration = studentAnalytics?.replayDuration || 0
                        const studentCategories = getStudentCategories(studentAnalytics, sequenceMode)
                        return (
                          <StudentSequenceRow
                            key={student.id}
                            student={student}
                            segments={segments}
                            totalDuration={totalDuration}
                            studentAnalytics={studentAnalytics}
                            sequenceMode={sequenceMode}
                            studentCategory={studentCategories}
                            onClick={() => handleStudentClick(student)}
                            getDisplayName={getDisplayName}
                            formatDuration={formatDuration}
                          />
                        )
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

     {/* ── Student Detail Modal ───────────────────────────────────────── */}
      {selectedStudent && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '12px', maxWidth: detailTab === 'replay' ? '1400px' : '900px', width: detailTab === 'replay' ? '95%' : '90%', height: '90vh', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'max-width 0.2s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '8px 16px 0', flexShrink: 0 }}>
              <button onClick={() => {
                const events = getEventsForUser(selectedStudent.id)
                const name = getDisplayName(selectedStudent)
                const data = { student_id: selectedStudent.id, name, session_id: sessionId, events: events || [] }
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `telemetry_student_${selectedStudent.id}_session_${sessionId}.json`
                a.click()
                URL.revokeObjectURL(url)
              }} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '6px', border: '1px solid #e0e0e0', background: 'white', fontSize: '11px', fontWeight: 500, color: '#444', cursor: 'pointer', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f5f5f7'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Telemetry JSON
              </button>
            </div>
            <StudentDetail
              student={buildStudentDetailData(selectedStudent, getEventsForUser(selectedStudent.id), sessionData)}
              rawEvents={getEventsForUser(selectedStudent.id)}
              precomputedAnalytics={analyticsById[selectedStudent.id]?.analytics || analyticsById[String(selectedStudent.id)]?.analytics || null}
              sessionData={sessionData}
              sessionId={sessionId}
              isGuiSession={isGui}
              studentUserId={selectedStudent.id}
              onBack={() => setSelectedStudent(null)}
              onTabChange={setDetailTab}
            />
          </div>
        </div>
      )}
    </div>
  )
}